import React, { Fragment, useState, useRef, useEffect } from 'react'
import '../../css/login.css'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { loginSuccess } from '../utils/redux/userSlice'
import { Col, Row, Card, CardBody } from 'reactstrap'
import Logo from '../../images/com_logo.png'
import ReCAPTCHA from 'react-google-recaptcha'
import { Toast } from 'primereact/toast'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import { API_URL } from '../utils/Constants/URLS'

const isProd = process.env.REACT_APP_IS_PROD === true

const Login1 = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [verifiedCaptcha, setVerifiedCaptcha] = useState(!isProd)
  const [firstLogin, setFirstLogin] = useState(false)
  const [isForgetPassword, setIsForgetPassword] = useState(false)
  const [id, setId] = useState('')
  const [sendingOtp, setSendingOtp] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const token = localStorage.getItem('token') || null

  const toast = useRef(null)
  const navigateTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current)
      }
    }
  }, [])

  const updatePassword = async e => {
    e.preventDefault()

    if (password !== confirmPassword) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Password and Confirm Password should be same',
        life: 3000
      })
      return
    }

    try {
      const res = await fetch(`${API_URL}users/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ password, firstLogin: false })
      })
      const result = await res.json()
      if (result.error === false) {
        toast.current?.show({
          severity: 'success',
          summary: 'Updated',
          detail: 'Password updated successfully',
          life: 2000
        })
        setFirstLogin(false)
        setPassword('')
        setConfirmPassword('')
        navigate('/')
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.message || 'Failed to update password',
          life: 3000
        })
      }
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong while updating password.',
        life: 3000
      })
    }
  }

  const handleUserLogin = async e => {
    e.preventDefault()

    try {
      const url = `${API_URL}users/login`
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })
      const result = await res.json()

      if (result.error === false) {
        if (result.data.firstLogin === true) {
          localStorage.setItem('token', result.data.token)
          setFirstLogin(true)
          setId(result.data.id)
          setPassword('')
          setConfirmPassword('')
          toast.current?.show({
            severity: 'info',
            summary: 'First Login',
            detail: 'Please set a new password to continue',
            life: 3000
          })
        } else {
          dispatch(loginSuccess(result.data))
          localStorage.setItem('token', result.data.token)
          toast.current?.show({
            severity: 'success',
            summary: 'Welcome',
            detail: 'Login successful',
            life: 1500
          })
          navigate('/dashboard')
        }
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Login failed',
          detail: result.message || 'Invalid credentials',
          life: 3000
        })
      }
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Login failed. Please try again.',
        life: 3000
      })
    }
  }

  // Show toast, then navigate after a short delay so the toast can render
  const handleSendOtp = e => {
    e.preventDefault()

    if (!email) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter your email',
        life: 3000
      })
      return
    }

    setSendingOtp(true)

    const myHeaders = new Headers()
    myHeaders.append('Content-Type', 'application/json')

    const raw = JSON.stringify({ email })

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    }

    fetch(`${API_URL}users/generate-otp`, requestOptions)
      .then(response => response.json())
      .then(result => {
        if (result.error === false) {
          // 1) Show toast on current page
          toast.current?.show({
            severity: 'success',
            summary: 'OTP Sent',
            detail: `OTP sent to ${email}`,
            life: 3000
          })
          // 2) Navigate after a brief delay (keep <Toast> mounted long enough to render)
          navigateTimeoutRef.current = setTimeout(() => {
            navigate('/verify-otp', { state: { email } })
          }, 1200)
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: result.message || 'Failed to send OTP.',
            life: 3000
          })
        }
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to send OTP. Please try again.',
          life: 3000
        })
      })
      .finally(() => setSendingOtp(false))
  }

  const handleReCaptcha = () => setVerifiedCaptcha(true)

  const showForgotView = !firstLogin && isForgetPassword
  const showLoginView = !firstLogin && !isForgetPassword

  return (
    <div className='login-page'>
      <Toast ref={toast} />

      <Card className={`mt-4 ${firstLogin === true ? 'w-96' : ''}`}>
        <CardBody className='p-4'>
          <Row className='logo'>
            <Col className='d-flex justify-content-center align-items-center' md={12}>
              <img src={Logo} alt='CRM Logo' />
            </Col>
          </Row>

          <form
            onSubmit={
              showForgotView
                ? handleSendOtp
                : showLoginView
                  ? handleUserLogin
                  : updatePassword
            }
            className='mt-4'
          >
            <Row className='mb-3'>
              <Col md={12}>
                <label>Email</label>
                <input
                  name='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className='form-control credentials mt-1'
                  type='email'
                  placeholder='Enter your email'
                  required
                />
              </Col>
            </Row>

            {/* First login: update password */}
            {firstLogin && (
              <Fragment>
                <Row className='mt-3 mb-3'>
                  <Col md={12}>
                    <label>New Password</label>
                    <input
                      name='password'
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className='form-control credentials mt-1'
                      type='password'
                      placeholder='Enter your new password'
                      required
                    />
                  </Col>
                </Row>
                <Row className='mt-3 mb-3'>
                  <Col md={12}>
                    <label>Confirm Password</label>
                    <input
                      name='confirmPassword'
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className='form-control credentials mt-1'
                      type='password'
                      placeholder='Confirm your password'
                      required
                    />
                  </Col>
                </Row>
                <Row className='mt-3'>
                  <center>
                    <Col md={6} sm={4}>
                      <button
                        className='btn btn-login w-100 justify-content-center align-items-center text-white'
                        type='submit'
                      >
                        Update Password
                      </button>
                    </Col>
                  </center>
                </Row>
              </Fragment>
            )}

            {/* Forgot password view: ONLY email + Send OTP */}
            {showForgotView && (
              <Fragment>
                <Row className='mt-1'>
                  <center>
                    <Col md={6} sm={4} className='mb-3'>
                      <button
                        className='btn btn-login w-100 justify-content-center align-items-center text-white'
                        type='submit'
                        disabled={sendingOtp}
                      >
                        {sendingOtp ? 'Sending...' : 'Send OTP'}
                      </button>
                    </Col>
                  </center>
                </Row>
                <Row>
                  <Col md={12} className='text-center'>
                    <button
                      type='button'
                      className='bg-transparent border-0 text-orange-400 pr-6'
                      onClick={() => {
                        setIsForgetPassword(false)
                        setVerifiedCaptcha(false)
                      }}
                    >
                      ← Back to Sign In
                    </button>
                  </Col>
                </Row>
              </Fragment>
            )}

            {/* Regular login view */}
            {showLoginView && (
              <Fragment>
                <Row className='mt-3 mb-3'>
                  <Col md={12}>
                    <label>Password</label>
                    <input
                      name='password'
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className='form-control credentials mt-1'
                      type='password'
                      placeholder='Enter your password'
                      required
                    />
                  </Col>
                </Row>

                <Row className='mt-3'>
                  <button
                    type='button'
                    className='bg-transparent border-0 text-orange-400 text-start pl-2'
                    onClick={() => {
                      setIsForgetPassword(true)
                      setVerifiedCaptcha(false)
                    }}
                  >
                    Forgot Password?
                  </button>
                </Row>

                {isProd && (
                  <Row className='mt-3'>
                    <ReCAPTCHA
                      sitekey='6Lc6jJMrAAAAALn-4Z2bZSiOx58LPOCzarQNPWKo'
                      onChange={handleReCaptcha}
                    />
                  </Row>
                )}

                {verifiedCaptcha && (
                  <Row className='mt-3'>
                    <center>
                      <Col md={6} sm={4}>
                        <button
                          className='btn btn-login w-100 justify-content-center align-items-center text-white'
                          type='submit'
                        >
                          Sign In
                        </button>
                      </Col>
                    </center>

                  </Row>
                )}
              </Fragment>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  )
}

export default Login1
